import { useContext, useMemo, useState } from "react"
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid"

import { Button, Field, Form } from "web/components"

import { LocalizationContext } from "web/contexts"

import { useFields, useFormSections } from "web/hooks/data"
import { useModals } from "web/hooks/modals"

import { getSectionsToShowWithOtherToggle } from "web/components/Form/helpers"

import { humanize } from "web/services/inflector"

function NewTopic({ subjectType, topic, records, respond }) {
  const [formValue, setFormValue] = useState({})
  const fields = useFields({ subjectType, topicId: topic.id })
  const sections = useFormSections({ subjectType, topicId: topic.id })

  const { configuredSections, otherSection, showOtherFieldsToggle } = useMemo(
    () => getSectionsToShowWithOtherToggle({ sections, fields }),
    [sections, fields]
  )

  const hasConfiguredSections = configuredSections.length !== 0

  async function handleSubmit(value) {
    await records.add({ ...value })
    respond()
  }

  return (
    <Form
      value={formValue}
      onChange={setFormValue}
      onSubmit={handleSubmit}
      fields={fields}
      render={(form) => {
        return (
          <div className="h-full">
            <div className="modal-content-above-commands">
              <div className="grid gap-y-2.5 divide-y divide-neutral-9">
                {configuredSections.map((section) => (
                  <Section
                    key={section.id}
                    section={section}
                    showSectionHeader={hasConfiguredSections}
                  />
                ))}
                {showOtherFieldsToggle && (
                  <OtherFieldsSection section={otherSection} />
                )}
              </div>
            </div>
            <div className="modal-commands">
              <div className="left-buttons">
                <Button onClick={form.submit} disableAfterClick>
                  Save
                </Button>
                <Button variant="tertiary_default" onClick={respond}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )
      }}
    />
  )
}

function Section({ section, showSectionHeader }) {
  return (
    <section className="grid gap-y-5 py-5 first:pt-0">
      {showSectionHeader && (
        <header className="text-lg font-bold text-neutral-3">
          {section.label}
        </header>
      )}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5">
        {section.fields.map((field) => (
          <Field key={field.id} fieldKey={field.key} />
        ))}
      </div>
    </section>
  )
}

function OtherFieldsSection({ section }) {
  const fieldCount = section.fields.length
  const [expanded, setExpanded] = useState(false)

  return (
    <section className="grid gap-y-5 py-5 first:pt-0">
      <header
        className="flex cursor-pointer items-center text-lg font-bold text-neutral-3 hover:text-feature-6"
        onClick={() => {
          setExpanded(!expanded)
        }}
      >
        <span className="-ml-2 h-8 w-8">
          {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </span>
        {`Other Fields (${fieldCount})`}
      </header>
      {expanded && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5">
          {section.fields.map((field) => (
            <Field key={field.id} fieldKey={field.key} />
          ))}
        </div>
      )}
    </section>
  )
}

export default function useNewTopicModal({ topic, ...restOfProps }) {
  const { showModal } = useModals()
  const localizationContext = useContext(LocalizationContext)

  return async function (records) {
    return await showModal({
      title: `New ${humanize(topic.label)} Record`,
      fullScreen: true,
      component: ({ respond }) => (
        <LocalizationContext.Provider value={localizationContext}>
          <NewTopic
            topic={topic}
            {...restOfProps}
            records={records}
            respond={respond}
          />
        </LocalizationContext.Provider>
      ),
    })
  }
}
